import React, { useState, useEffect, useRef } from 'react'
import {
  Container,
  Table,
  Label,
  Input,
  Button,
  Navbar
} from 'reactstrap'
import { MdClose } from 'react-icons/md'
import { Editor, Loader } from 'components'
import { showAlert, showSuccess } from 'utils/toast'
import { createMail } from 'api/mailApi'
import { fetchDefaultSiteConfig } from 'api/siteConfigApi'
import colors from 'assets/colors'
import { useSearchParams } from 'react-router-dom'

const MailCreate = ({ targetUsername = '', type: propType, onClose }) => {
  const editorRef = useRef()
  const [searchParams] = useSearchParams()

  const resolvedType = ['partnerReceived', 'partnerSent'].includes(propType)
    ? 'partner'
    : 'member'

  const [loading, setLoading] = useState(true)
  const [grades, setGrades] = useState([])
  const [mail, setMail] = useState(getInitialMail())
  const [editorKey, setEditorKey] = useState(Date.now())

  function getInitialMail() {
    return {
      type: resolvedType,
      category: 'individual',
      partner: '',
      individual: targetUsername,
      grade: [],
      name: '',
      content: ''
    }
  }

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const { success, data, message } = await fetchDefaultSiteConfig()
        if (!success) throw new Error(message)
        setGrades(data.grades || [])
      } catch (err) {
        console.error(err)
        showAlert(err.message)
      } finally {
        setLoading(false)
      }
    }
    loadConfig()
  }, [])

  const handleReset = () => {
    setMail(getInitialMail())
    setEditorKey(Date.now())
  }

  const handleChange = ({ target }) => {
    const { name, value, type, checked } = target
    setMail(prev => {
      const updated = { ...prev }
      let newVal = value

      if (type === 'checkbox') {
        const list = prev[name] || []
        newVal = checked
          ? [...list, value]
          : list.filter(item => item !== value)
      }

      if (type === 'number') newVal = Number(value)
      if (type === 'radio') newVal = value === 'true'

      updated[name] = newVal
      return updated
    })
  }

  const handleSave = async () => {
    if (!mail.name) return showAlert('제목을 입력해주세요')

    setLoading(true)
    try {
      const content = editorRef.current.getContent()
      const { success, message } = await createMail({ ...mail, content })
      if (!success) throw new Error(message)
      showSuccess('쪽지를 보냈습니다')
      handleReset()
      onClose?.()
    } catch (err) {
      console.error(err)
      showAlert(err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Navbar style={{ backgroundColor: colors.primary, padding: '1rem' }}>
        <h5 className="m-0">
          {resolvedType === 'member' ? '회원' : '파트너'} 쪽지쓰기
        </h5>
        <MdClose
          className="ms-auto clickable-icon"
          size={20}
          onClick={onClose}
        />
      </Navbar>

      <Container className="page-content pb-5">
        {loading ? (
          <Loader />
        ) : (
          <Table bordered className="mt-4">
            <tbody>
              {/* Category Selection */}
              <tr>
                <td className="form-header text-center">구분</td>
                <td>
                  <Input
                    type="select"
                    name="category"
                    value={mail.category}
                    onChange={handleChange}
                    bsSize="sm"
                  >
                    <option value="individual">개별</option>
                    {resolvedType === 'member' && <option value="grade">등급별</option>}
                    <option value="all">{resolvedType === 'member' ? '전체 회원' : '전체 파트너'}</option>
                  </Input>
                </td>
              </tr>

              {/* Recipient */}
              <tr>
                <td className="form-header text-center">수신</td>
                <td>
                  {mail.category === 'individual' && (
                    <Input
                      bsSize="sm"
                      name="individual"
                      value={mail.individual}
                      placeholder={`${resolvedType === 'member' ? '회원' : '파트너'} 아이디`}
                      onChange={handleChange}
                    />
                  )}

                  {mail.category === 'grade' && grades.map(g => (
                    <Label key={g.grade} check className="me-3">
                      <Input
                        type="checkbox"
                        name="grade"
                        value={g.grade}
                        onChange={handleChange}
                      />
                      {`${g.grade}레벨`}
                    </Label>
                  ))}

                  {/* 카테고리 all일 때는 아무것도 표시하지 않음 */}
                </td>
              </tr>

              {/* Subject */}
              <tr>
                <td className="form-header text-center">제목</td>
                <td>
                  <Input
                    bsSize="sm"
                    name="name"
                    value={mail.name}
                    placeholder="제목"
                    onChange={handleChange}
                  />
                </td>
              </tr>

              {/* Content */}
              <tr>
                <td className="form-header text-center">내용</td>
                <td>
                  <Editor ref={editorRef} key={editorKey} value={mail.content} />
                </td>
              </tr>
            </tbody>
          </Table>
        )}

        <div className="action-bar-container show">
          <div className="action-bar d-flex justify-content-end">
            <Button color="warning" className="me-2" onClick={onClose}>
              닫기
            </Button>
            <Button color="primary" onClick={handleSave}>
              보내기
            </Button>
          </div>
        </div>
      </Container>
    </>
  )
}

export default MailCreate
